import React, { useState, useMemo, useCallback } from "react";
import { Tooltip } from "@material-ui/core";
import { getActionCardColors } from "./ActionCardColors";

const DrawCardDeck = ({ 
  onDraw, 
  canDraw, 
  handFull,
  styleCardSize = { width: "150px", height: "210px" }
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const colors = useMemo(() => getActionCardColors("default"), []);

  const cardStack = useMemo(() => [
    { offset: 0, zIndex: 3, opacity: 1 },
    { offset: 4, zIndex: 2, opacity: 0.7 },
    { offset: 8, zIndex: 1, opacity: 0.5 }
  ], []);

  const handleDraw = useCallback(() => {
    if (canDraw && !handFull && onDraw) {
      onDraw();
    }
  }, [canDraw, handFull, onDraw]);

  const tooltipText = useMemo(() => {
    if (handFull) return "Your hand is full";
    if (!canDraw) return "Not your turn";
    return "Draw an action card";
  }, [handFull, canDraw]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const containerClassName = useMemo(() => 
    `draw-card-deck-container ${(canDraw && !handFull) ? '' : 'draw-card-deck-container-disabled'}`,
    [canDraw, handFull]
  );

  // Use forwardRef wrapper to avoid findDOMNode warning in StrictMode
  const TooltipWrapper = React.forwardRef((props, ref) => (
    <div 
      ref={ref}
      {...props}
      className={containerClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="draw-card-deck-stack relative" style={{ width: styleCardSize.width, height: styleCardSize.height }}>
        {cardStack.map((card, index) => (
          <div
            key={index}
            className={`stock-card card draw-card-deck-card ${isHovered && canDraw && !handFull ? 'draw-card-deck-card-hover' : ''}`}
            style={{
              top: `${card.offset}px`,
              left: `${card.offset}px`,
              width: styleCardSize.width,
              height: styleCardSize.height,
              background: colors.bg,
              borderColor: colors.border,
              boxShadow: `0 4px 12px ${colors.border}40`,
              zIndex: card.zIndex,
              opacity: card.opacity
            }}
          >
            <div 
              className="stock-card-top flex items-center gap-sm p-sm"
              style={{
                background: colors.header,
                borderBottom: `2px solid ${colors.border}`,
                height: '38px'
              }}
            >
              <span className="text-base leading-tight">{colors.emoji}</span>
              <span className="flex-1 font-bold text-sm leading-tight">Action Cards</span>
            </div>

            <div className="w-full flex-1 flex flex-col items-center justify-center p-lg box-border">
              <div className="text-2xl opacity-80 mb-sm">
                {colors.emoji}
              </div>
            </div>
          </div>
        ))}

        {isHovered && (
          <div 
            className="draw-card-deck-overlay"
            style={{
              width: styleCardSize.width,
              height: styleCardSize.height
            }}
          >
            {handFull ? (
              <div className="draw-card-deck-button draw-card-deck-button-disabled">
                <div className="draw-card-deck-button-label">
                  HAND FULL
                </div>
                <div className="draw-card-deck-button-value">
                  ⛔
                </div>
              </div>
            ) : canDraw ? (
              <button
                className="draw-card-deck-button"
                onClick={handleDraw}
                style={{ backgroundColor: colors.border }}
              >
                <div className="draw-card-deck-button-label">
                  ⚡PICK UP
                </div>
                <div className="draw-card-deck-button-value-small">
                  CARD
                </div>
              </button>
            ) : (
              <div className="draw-card-deck-button draw-card-deck-button-disabled">
                <div className="draw-card-deck-button-label">
                  NO ⚡
                </div>
                <div className="draw-card-deck-button-value-small">
                  NOT YOUR TURN
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  ));

  return (
    <Tooltip title={tooltipText} arrow placement="top">
      <TooltipWrapper />
    </Tooltip>
  );
};

export default DrawCardDeck;

